import Lottie from "lottie-react";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { useEffect, useState } from "react";

interface CircularAnimationLoaderProps {
  lightModeAnimation: unknown;
  darkModeAnimation: unknown;
  loadingTexts: string[];
  size?: number;
}

export const CircularAnimationLoader = ({
  lightModeAnimation,
  darkModeAnimation,
  loadingTexts,
  size = 74,
}: CircularAnimationLoaderProps) => {
  const appearanceMode = useSelector(
    (state: RootState) => state.themeProvider.appearanceMode
  );

  const [displayText, setDisplayText] = useState(loadingTexts[0] + ".");
  const [dots, setDots] = useState(1);
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [showInitialDots, setShowInitialDots] = useState(true);

  useEffect(() => {
    if (showInitialDots) {
      const dotInterval = setInterval(() => {
        setDots((prev) => {
          const nextDots = prev + 1;
          if (nextDots > 3) {
            setShowInitialDots(false);
            return 0;
          }
          return nextDots;
        });
      }, 400);

      return () => clearInterval(dotInterval);
    }
  }, [showInitialDots]);

  useEffect(() => {
    if (!showInitialDots) {
      setDots(0);
      setDisplayText(loadingTexts[currentTextIndex]);

      const dotTimers = [
        setTimeout(() => {
          setDots(1);
          setDisplayText(loadingTexts[currentTextIndex] + ".");
        }, 1000),
        setTimeout(() => {
          setDots(2);
          setDisplayText(loadingTexts[currentTextIndex] + "..");
        }, 2000),
        setTimeout(() => {
          setDots(3);
          setDisplayText(loadingTexts[currentTextIndex] + "...");
        }, 3000),
      ];

      const textTimer = setTimeout(() => {
        setCurrentTextIndex((prev) => (prev + 1) % loadingTexts.length);
      }, 4000);

      return () => {
        dotTimers.forEach((timer) => clearTimeout(timer));
        clearTimeout(textTimer);
      };
    }
  }, [currentTextIndex, showInitialDots, loadingTexts]);

  useEffect(() => {
    if (showInitialDots) {
      setDisplayText(loadingTexts[currentTextIndex] + ".".repeat(dots));
    }
  }, [dots, showInitialDots, currentTextIndex, loadingTexts]);

  const animationData =
    appearanceMode === "dark" ? darkModeAnimation : lightModeAnimation;

  return (
    <div className="flex flex-col items-center justify-center gap-4">
      <div style={{ width: size, height: size }}>
        <Lottie animationData={animationData} loop={true} autoplay={true} />
      </div>
      <div className="text-xl text-verba-indigo dark:text-verba-indigo-dk min-h-[28px] flex items-center justify-center transition-opacity duration-300">
        <span className={showInitialDots ? "tracking-wider" : ""}>
          {displayText}
        </span>
      </div>
    </div>
  );
};
